// QR Code (ISO/IEC 18004) in byte mode: the authenticator-app link of two-factor sign-in
// and the QR fields of marketplace ZPL labels, drawn by the browser. Pure: text in, module
// matrix out. Tables and placement follow the standard; any of the eight masks gives a valid
// symbol, the penalty rules only pick the one scanners like best.

export const QR_ERROR_LEVELS = ['L', 'M', 'Q', 'H'] as const;
export type QrErrorLevel = (typeof QR_ERROR_LEVELS)[number];

export interface QrCode {
  readonly version: number;
  /** modules per side (21 for version 1 … 177 for version 40) */
  readonly size: number;
  readonly level: QrErrorLevel;
  readonly mask: number;
  /** modules[y][x], true = dark; no quiet zone */
  readonly modules: readonly (readonly boolean[])[];
}

const LEVEL_INDEX: Readonly<Record<QrErrorLevel, number>> = { L: 0, M: 1, Q: 2, H: 3 };
const FORMAT_BITS: Readonly<Record<QrErrorLevel, number>> = { L: 1, M: 0, Q: 3, H: 2 };

// error correction codewords per block, and number of blocks, by level and version (index 0 unused)
const ECC_PER_BLOCK: readonly (readonly number[])[] = [
  [-1, 7, 10, 15, 20, 26, 18, 20, 24, 30, 18, 20, 24, 26, 30, 22, 24, 28, 30, 28, 28, 28, 28, 30, 30, 26, 28, 30, 30, 30, 30, 30, 30, 30, 30, 30, 30, 30, 30, 30, 30],
  [-1, 10, 16, 26, 18, 24, 16, 18, 22, 22, 26, 30, 22, 22, 24, 24, 28, 28, 26, 26, 26, 26, 28, 28, 28, 28, 28, 28, 28, 28, 28, 28, 28, 28, 28, 28, 28, 28, 28, 28, 28],
  [-1, 13, 22, 18, 26, 18, 24, 18, 22, 20, 24, 28, 26, 24, 20, 30, 24, 28, 28, 26, 30, 28, 30, 30, 30, 30, 28, 30, 30, 30, 30, 30, 30, 30, 30, 30, 30, 30, 30, 30, 30],
  [-1, 17, 28, 22, 16, 22, 28, 26, 26, 24, 28, 24, 28, 22, 24, 24, 30, 28, 28, 26, 28, 30, 24, 30, 30, 30, 30, 30, 30, 30, 30, 30, 30, 30, 30, 30, 30, 30, 30, 30, 30],
];
const BLOCKS: readonly (readonly number[])[] = [
  [-1, 1, 1, 1, 1, 1, 2, 2, 2, 2, 4, 4, 4, 4, 4, 6, 6, 6, 6, 7, 8, 8, 9, 9, 10, 12, 12, 12, 13, 14, 15, 16, 17, 18, 19, 19, 20, 21, 22, 24, 25],
  [-1, 1, 1, 1, 2, 2, 4, 4, 4, 5, 5, 5, 8, 9, 9, 10, 10, 11, 13, 14, 16, 17, 17, 18, 20, 21, 23, 25, 26, 28, 29, 31, 33, 35, 37, 38, 40, 43, 45, 47, 49],
  [-1, 1, 1, 2, 2, 4, 4, 6, 6, 8, 8, 8, 10, 12, 16, 12, 17, 16, 18, 21, 20, 23, 23, 25, 27, 29, 34, 34, 35, 38, 40, 43, 45, 48, 51, 53, 56, 59, 62, 65, 68],
  [-1, 1, 1, 2, 4, 4, 4, 5, 6, 8, 8, 11, 11, 16, 16, 18, 16, 19, 21, 25, 25, 25, 34, 30, 32, 35, 37, 40, 42, 45, 48, 51, 54, 57, 60, 63, 66, 70, 74, 77, 81],
];

/** Modules available for data and error correction (everything but the function patterns). */
function rawDataModules(version: number): number {
  let result = (16 * version + 128) * version + 64;
  if (version >= 2) {
    const align = Math.floor(version / 7) + 2;
    result -= (25 * align - 10) * align - 55;
    if (version >= 7) result -= 36;
  }
  return result;
}

function dataCodewords(version: number, level: QrErrorLevel): number {
  const l = LEVEL_INDEX[level];
  return Math.floor(rawDataModules(version) / 8) - ECC_PER_BLOCK[l]![version]! * BLOCKS[l]![version]!;
}

// --- Reed–Solomon over GF(2^8) with x^8 + x^4 + x^3 + x^2 + 1 ----------------------------

function gfMultiply(x: number, y: number): number {
  let z = 0;
  for (let i = 7; i >= 0; i--) {
    z = (z << 1) ^ ((z >>> 7) * 0x11d);
    z ^= ((y >>> i) & 1) * x;
  }
  return z & 0xff;
}

function rsDivisor(degree: number): number[] {
  const result = new Array<number>(degree).fill(0);
  result[degree - 1] = 1;
  let root = 1;
  for (let i = 0; i < degree; i++) {
    for (let j = 0; j < result.length; j++) {
      result[j] = gfMultiply(result[j]!, root);
      if (j + 1 < result.length) result[j] = result[j]! ^ result[j + 1]!;
    }
    root = gfMultiply(root, 0x02);
  }
  return result;
}

function rsRemainder(data: readonly number[], divisor: readonly number[]): number[] {
  const result = new Array<number>(divisor.length).fill(0);
  for (const byte of data) {
    const factor = byte ^ result.shift()!;
    result.push(0);
    divisor.forEach((coefficient, i) => {
      result[i] = result[i]! ^ gfMultiply(coefficient, factor);
    });
  }
  return result;
}

// --- encoding -----------------------------------------------------------------------------

function utf8(text: string): number[] {
  return [...new TextEncoder().encode(text)];
}

/** Data codewords of the text in byte mode, padded to the version's capacity. */
function dataBits(bytes: readonly number[], version: number, level: QrErrorLevel): number[] {
  const bits: number[] = [];
  const push = (value: number, length: number) => {
    for (let i = length - 1; i >= 0; i--) bits.push((value >>> i) & 1);
  };
  push(0b0100, 4);
  push(bytes.length, version < 10 ? 8 : 16);
  for (const b of bytes) push(b, 8);
  const capacity = dataCodewords(version, level) * 8;
  push(0, Math.min(4, capacity - bits.length));
  push(0, (8 - (bits.length % 8)) % 8);
  const codewords: number[] = [];
  for (let i = 0; i < bits.length; i += 8) codewords.push(bits.slice(i, i + 8).reduce((acc, bit) => (acc << 1) | bit, 0));
  for (let pad = 0xec; codewords.length < capacity / 8; pad ^= 0xec ^ 0x11) codewords.push(pad);
  return codewords;
}

/** Splits into blocks, appends each block's error correction, interleaves. */
function withErrorCorrection(data: readonly number[], version: number, level: QrErrorLevel): number[] {
  const l = LEVEL_INDEX[level];
  const blocks = BLOCKS[l]![version]!;
  const eccLength = ECC_PER_BLOCK[l]![version]!;
  const raw = Math.floor(rawDataModules(version) / 8);
  const shortBlocks = blocks - (raw % blocks);
  const shortLength = Math.floor(raw / blocks);
  const divisor = rsDivisor(eccLength);
  const all: number[][] = [];
  for (let i = 0, k = 0; i < blocks; i++) {
    const length = shortLength - eccLength + (i < shortBlocks ? 0 : 1);
    const part = data.slice(k, k + length);
    k += length;
    const ecc = rsRemainder(part, divisor);
    // short blocks get a placeholder so every block has the same length when interleaving
    if (i < shortBlocks) part.push(-1);
    all.push([...part, ...ecc]);
  }
  const result: number[] = [];
  for (let i = 0; i < all[0]!.length; i++) {
    for (const block of all) if (block[i] !== -1) result.push(block[i]!);
  }
  return result;
}

// --- matrix -------------------------------------------------------------------------------

class Matrix {
  readonly version: number;
  readonly size: number;
  readonly modules: boolean[][];
  readonly reserved: boolean[][];

  constructor(version: number) {
    this.version = version;
    this.size = version * 4 + 17;
    this.modules = Array.from({ length: this.size }, () => new Array<boolean>(this.size).fill(false));
    this.reserved = Array.from({ length: this.size }, () => new Array<boolean>(this.size).fill(false));
  }

  set(x: number, y: number, dark: boolean): void {
    this.modules[y]![x] = dark;
    this.reserved[y]![x] = true;
  }
}

function alignmentPositions(version: number): number[] {
  if (version === 1) return [];
  const count = Math.floor(version / 7) + 2;
  const step = Math.floor((version * 8 + count * 3 + 5) / (count * 4 - 4)) * 2;
  const result = [6];
  for (let pos = version * 4 + 17 - 7; result.length < count; pos -= step) result.splice(1, 0, pos);
  return result;
}

function drawFunctionPatterns(m: Matrix): void {
  for (let i = 0; i < m.size; i++) {
    m.set(6, i, i % 2 === 0);
    m.set(i, 6, i % 2 === 0);
  }
  const finder = (cx: number, cy: number) => {
    for (let dy = -4; dy <= 4; dy++) {
      for (let dx = -4; dx <= 4; dx++) {
        const x = cx + dx;
        const y = cy + dy;
        if (x < 0 || y < 0 || x >= m.size || y >= m.size) continue;
        const distance = Math.max(Math.abs(dx), Math.abs(dy));
        m.set(x, y, distance !== 2 && distance !== 4);
      }
    }
  };
  finder(3, 3);
  finder(m.size - 4, 3);
  finder(3, m.size - 4);
  const positions = alignmentPositions(m.version);
  const last = positions.length - 1;
  positions.forEach((cx, i) =>
    positions.forEach((cy, j) => {
      if ((i === 0 && j === 0) || (i === 0 && j === last) || (i === last && j === 0)) return;
      for (let dy = -2; dy <= 2; dy++) for (let dx = -2; dx <= 2; dx++) m.set(cx + dx, cy + dy, Math.max(Math.abs(dx), Math.abs(dy)) !== 1);
    }),
  );
  drawFormatBits(m, 'M', 0); // reserves the area; drawn for real after masking
  if (m.version >= 7) {
    let rem = m.version;
    for (let i = 0; i < 12; i++) rem = (rem << 1) ^ ((rem >>> 11) * 0x1f25);
    const bits = (m.version << 12) | rem;
    for (let i = 0; i < 18; i++) {
      const dark = ((bits >>> i) & 1) !== 0;
      const a = m.size - 11 + (i % 3);
      const b = Math.floor(i / 3);
      m.set(a, b, dark);
      m.set(b, a, dark);
    }
  }
}

function drawFormatBits(m: Matrix, level: QrErrorLevel, mask: number): void {
  const data = (FORMAT_BITS[level] << 3) | mask;
  let rem = data;
  for (let i = 0; i < 10; i++) rem = (rem << 1) ^ ((rem >>> 9) * 0x537);
  const bits = ((data << 10) | rem) ^ 0x5412;
  const bit = (i: number) => ((bits >>> i) & 1) !== 0;
  for (let i = 0; i <= 5; i++) m.set(8, i, bit(i));
  m.set(8, 7, bit(6));
  m.set(8, 8, bit(7));
  m.set(7, 8, bit(8));
  for (let i = 9; i < 15; i++) m.set(14 - i, 8, bit(i));
  for (let i = 0; i < 8; i++) m.set(m.size - 1 - i, 8, bit(i));
  for (let i = 8; i < 15; i++) m.set(8, m.size - 15 + i, bit(i));
  m.set(8, m.size - 8, true);
}

function placeCodewords(m: Matrix, codewords: readonly number[]): void {
  let i = 0;
  const total = codewords.length * 8;
  for (let right = m.size - 1; right >= 1; right -= 2) {
    if (right === 6) right = 5;
    for (let vertical = 0; vertical < m.size; vertical++) {
      for (let j = 0; j < 2; j++) {
        const x = right - j;
        const upward = ((right + 1) & 2) === 0;
        const y = upward ? m.size - 1 - vertical : vertical;
        if (!m.reserved[y]![x] && i < total) {
          m.modules[y]![x] = ((codewords[i >>> 3]! >>> (7 - (i & 7))) & 1) !== 0;
          i++;
        }
      }
    }
  }
}

const MASKS: readonly ((x: number, y: number) => boolean)[] = [
  (x, y) => (x + y) % 2 === 0,
  (_x, y) => y % 2 === 0,
  (x) => x % 3 === 0,
  (x, y) => (x + y) % 3 === 0,
  (x, y) => (Math.floor(x / 3) + Math.floor(y / 2)) % 2 === 0,
  (x, y) => ((x * y) % 2) + ((x * y) % 3) === 0,
  (x, y) => (((x * y) % 2) + ((x * y) % 3)) % 2 === 0,
  (x, y) => (((x + y) % 2) + ((x * y) % 3)) % 2 === 0,
];

function applyMask(m: Matrix, mask: number): void {
  const flip = MASKS[mask]!;
  for (let y = 0; y < m.size; y++) for (let x = 0; x < m.size; x++) if (!m.reserved[y]![x] && flip(x, y)) m.modules[y]![x] = !m.modules[y]![x];
}

/** Penalty (rules N1–N4): runs, 2×2 blocks, finder look-alikes and dark/light balance. */
function penalty(m: Matrix): number {
  const size = m.size;
  const at = (x: number, y: number) => m.modules[y]![x]!;
  let score = 0;
  const finderLike = [true, false, true, true, true, false, true];
  for (const horizontal of [true, false]) {
    for (let a = 0; a < size; a++) {
      let run = 1;
      for (let b = 1; b <= size; b++) {
        const same = b < size && (horizontal ? at(b, a) === at(b - 1, a) : at(a, b) === at(a, b - 1));
        if (same) run++;
        else {
          if (run >= 5) score += 3 + (run - 5);
          run = 1;
        }
      }
      for (let b = 0; b + 7 <= size; b++) {
        const matches = finderLike.every((dark, k) => (horizontal ? at(b + k, a) : at(a, b + k)) === dark);
        if (!matches) continue;
        const light = (from: number, to: number) => {
          for (let k = from; k < to; k++) {
            if (k < 0 || k >= size) continue;
            if (horizontal ? at(k, a) : at(a, k)) return false;
          }
          return true;
        };
        if (light(b - 4, b) || light(b + 7, b + 11)) score += 40;
      }
    }
  }
  for (let y = 0; y + 1 < size; y++) {
    for (let x = 0; x + 1 < size; x++) {
      const c = at(x, y);
      if (c === at(x + 1, y) && c === at(x, y + 1) && c === at(x + 1, y + 1)) score += 3;
    }
  }
  let dark = 0;
  for (const row of m.modules) for (const cell of row) if (cell) dark++;
  const total = size * size;
  score += Math.max(0, Math.ceil(Math.abs(dark * 20 - total * 10) / total) - 1) * 10;
  return score;
}

export class QrCapacityError extends RangeError {
  override readonly name = 'QrCapacityError';
}

/** Smallest QR symbol for the text (UTF-8 bytes) at the error level; throws when it does not fit version 40. */
export function encodeQr(text: string, level: QrErrorLevel = 'M'): QrCode {
  const bytes = utf8(text);
  let version = 1;
  for (; version <= 40; version++) {
    const header = 4 + (version < 10 ? 8 : 16);
    if (header + bytes.length * 8 <= dataCodewords(version, level) * 8) break;
  }
  if (version > 40) throw new QrCapacityError(`text too long for a QR code at level ${level}: ${bytes.length} bytes`);
  const codewords = withErrorCorrection(dataBits(bytes, version, level), version, level);

  let best: { mask: number; score: number; modules: boolean[][] } | null = null;
  for (let mask = 0; mask < 8; mask++) {
    const m = new Matrix(version);
    drawFunctionPatterns(m);
    placeCodewords(m, codewords);
    applyMask(m, mask);
    drawFormatBits(m, level, mask);
    const score = penalty(m);
    if (!best || score < best.score) best = { mask, score, modules: m.modules };
  }
  return { version, size: version * 4 + 17, level, mask: best!.mask, modules: best!.modules };
}

/** SVG path of the dark modules (one unit per module, quiet zone not included). */
export function qrPath(code: QrCode, offset = 0): string {
  const parts: string[] = [];
  code.modules.forEach((row, y) => {
    for (let x = 0; x < code.size; ) {
      if (!row[x]) {
        x++;
        continue;
      }
      let run = 1;
      while (x + run < code.size && row[x + run]) run++;
      parts.push(`M${x + offset} ${y + offset}h${run}v1h-${run}z`);
      x += run;
    }
  });
  return parts.join('');
}
